import { debounce } from 'lodash';
import React, { useContext, useEffect } from 'react';

import { UiStateContext } from '../../contexts/ui-state/UiStateContext';
import { FeatureToggle } from '../../models/app-data-model';
import useFeatureToggle from '../use-feature-toggle/useFeatureToggle';

/**
 * This hook encapsulates our pageHasScrolled handler
 * It is a top level hook that sets the result on our global context instead of returning a result.
 * It should only be used at the top-most (page) levels. (Homepage, and generated pages).
 * NOTE: as a result, this MUST be used with a UIStateContext
 * @returns
 */
function usePageHasScrolled(pageRef: React.RefObject<HTMLElement>, staticToggles: FeatureToggle[]): void {
  const uiStateContext = useContext(UiStateContext);
  const hidingHeaderFeatureEnabled = useFeatureToggle('header-hiding-on-scroll', staticToggles);

  /**
   * This hook adds a event handler for scroll which updates our uistatecontext whenever the page has been scrolled
   */
  useEffect(() => {
    // scroll handlers fire RIDICULOUSLY frequent.
    // especially on macs and things with smooth scroll.
    // debounce lets us say "just throttle it to once every 300ms" or w/e
    // really cuts down on the rerendering/jumping/nastiness that the frequency causes the handler to freakout
    const handler = debounce(
      (pageHasScrolled: boolean) => {
        uiStateContext.setPageHasScrolled?.(pageHasScrolled);
      },
      /* istanbul ignore next */
      hidingHeaderFeatureEnabled ? 15 : 0
    );

    // todo: what if we could also factor into the boolean that if we animated we would then be back at the top of the page, like if two numbers evaluate to - negative then this is false.
    const handleScroll = (): void | undefined => handler(window?.scrollY > (pageRef?.current?.clientTop || 0));
    window?.addEventListener('scroll', handleScroll, { passive: true });
    return () => window?.removeEventListener('scroll', handleScroll);
  });
}

export default usePageHasScrolled;
